import React, { createContext, useEffect, useState } from 'react';
import { useHistory } from "react-router-dom";
import SLogin, { AuthResponse } from '../services/api/Login';
import User, { IUser } from '../services/api/User';
import Request from '../services/api/Request';

import { clearOverviewCache } from '../services/api/Dashboard';
import { clearChannelCache } from '../services/api/Channel';
import { clearChannelTypeCache } from '../services/api/ChannelType';
import { clearMonthCache } from '../services/api/Month';
import { clearYearCache } from '../services/api/Year';
import { invalidateChannelCache } from '../pages/Dashboard/components/channelCache';

interface AuthContextData {
    logged: boolean;
    user: IUser | null;
    signInSaml: Function;
    Login(data: IUser): Promise<AuthResponse>;
    Logout(clearURL?: boolean): Promise<boolean>;
    UpdateAuthUser(): Promise<void>;
    LoginSaml(): Promise<void>;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

export const AuthProvider: React.FC = ({ children }) => {
    const history = useHistory();
    const [user, setUser] = useState<IUser | null>(null);
    const [loading, setLoading] = useState(true);

    // ============================================================
    // Inicialização
    // O token NÃO é lido pelo React.
    // O browser envia automaticamente o cookie HttpOnly  para a API.
    // ============================================================
    useEffect(() => {
        InitializeAuthentication();
    }, []);


    async function InitializeAuthentication() {
        try {
            await LoginSaml();
        }
        catch {
            setUser(null);
        }
        finally {
            setLoading(false);
        }
    }


    // ============================================================
    // LOGIN NORMAL
    // Idealmente o backend também deve criar o cookie HttpOnly.
    // ============================================================
    async function Login(data: IUser) {
        const response: AuthResponse = await SLogin.Authentication(data);
        if (response.status === 200) {
            SetAndStoreUser(response.user);
        }

        return response;
    }


    // ============================================================
    // LOGOUT
    // ============================================================
    async function Logout(clearURL: boolean = false): Promise<boolean> {
        try {
            // O backend remove o cookie HttpOnly.
            // O React não consegue executar:
            // document.cookie = ...
            // await Request.Post("/api/login/logout");
            await SLogin.Logout();
        }
        catch {
        }


        // ========================================================
        // IMPORTANTE:
        // Não existe mais:
        // localStorage.removeItem("UserAuth")
        // para token.
        // Estamos removendo também qualquer versão antiga.
        // ========================================================
        window.localStorage.removeItem(
            "UserAuth"
        );

        // Não existe mais bearer local.
        // Request.BearerToken = '';

        clearOverviewCache();
        clearChannelCache();
        clearChannelTypeCache();
        clearMonthCache();
        clearYearCache();
        invalidateChannelCache();

        setUser(null);

        if (clearURL) {
            history.push({
                pathname: "/"
            });
        }

        return false;
    }

    // ============================================================
    // LOGIN SAML
    // ============================================================
    function signInSaml() {
        window.location.href = process.env.REACT_APP_DOMAIN + '/saml/login';
    }

    // ============================================================
    // AUTENTICAÇÃO SAML
    // O React NÃO lê SamlAuth.
    // O backend recebe automaticamente:
    // Cookie: SamlAuth=...
    // e retorna os dados do usuário.
    // ============================================================
    async function LoginSaml(): Promise<void> {
        try {
            const response: IUser = await User.GetInfo();
            if (response) {
                SetAndStoreUser(
                    response
                );

                return;
            }

            setUser(null);
        }
        catch {
            setUser(null);

            throw new Error(
                "Usuário não autenticado."
            );
        }
    }

    // ============================================================
    // ATUALIZA USUÁRIO
    // ============================================================
    async function UpdateAuthUser(): Promise<void> {
        if (!user?.id)
            return;

        const response: IUser = await User.Get(user.id);
        SetAndStoreUser(
            response
        );
    }


    // ============================================================
    // ARMAZENA SOMENTE DADOS DO USUÁRIO
    // NÃO armazena token.
    // ============================================================
    function SetAndStoreUser(
        newUser: IUser
    ) {
        // --------------------------------------------------------
        // Remover qualquer bearer eventualmente existente de versões antigas.
        // --------------------------------------------------------
        delete (newUser as any).bearer;

        newUser.basicLayout =
            [
                "Sender",
                "Approver",
                "Viewer"
            ].indexOf(
                newUser.profile.name
            ) > -1;

        window.localStorage.setItem(
            "UserAuth",
            JSON.stringify(newUser)
        );

        setUser(newUser);
    }

    // ============================================================
    // Enquanto está inicializando a sessão
    // ============================================================
    if (loading) {
        return null;
    }

    return (
        <AuthContext.Provider
            value={{
                logged: !!user,
                user,
                Login,
                Logout,
                signInSaml,
                UpdateAuthUser,
                LoginSaml
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export default AuthContext;